import {
  Box,
  FormHelperText,
  Grid,
  TextField,
  useTheme,
} from "@mui/material";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import SearchableSelect from "app/components/SearchableSelect";
import { DateTime } from "luxon";
import React from "react";
import { useTranslation } from "react-i18next";
import { useStyles } from "../styles";

export type FieldType = "text" | "number" | "textarea" | "time" | "select";

export type SelectOption = {
  value: string | number;
  label: string;
};

interface Props {
  type: FieldType;
  label: React.ReactNode;
  value: any;
  onChange: (value: any) => void;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  rows?: number;
  xs?: number;
  md?: number | boolean;
  options?: SelectOption[];
  loading?: boolean;
  selectPlaceholder?: string;
}

const FormField: React.FC<Props> = ({
  type,
  label,
  value,
  onChange,
  placeholder,
  error,
  disabled,
  required,
  rows = 4,
  xs = 12,
  md,
  options = [],
  loading,
  selectPlaceholder,
}) => {
  const styles = useStyles();
  const { i18n } = useTranslation();
  const direction = useTheme().direction;
  const helperTextStyle = {
    backgroundColor: "#F5F8FF",
    margin: "0px",
    padding: "0px",
    textAlign: i18n.dir() === "ltr" ? ("left" as const) : ("right" as const),
  };
  const hasError = !!error;

  const renderField = () => {
    if (type === "select") {
      return (
        <SearchableSelect
          options={options}
          value={value ?? ""}
          onChange={(v) => onChange(v)}
          disabled={disabled}
          error={hasError}
          placeholder={selectPlaceholder || placeholder || ""}
          loading={loading}
          className={styles.textEnabled}
        />
      );
    }

    if (type === "time") {
      return (
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <TimePicker
            className={styles.textEnabled}
            onChange={(d: any) => {
              const date = DateTime.fromISO(d);
              if (date.isValid) {
                onChange(date.toJSDate().toISOString());
              }
            }}
            value={value ? new Date(value) : null}
            ampm={false}
            disabled={disabled}
            renderInput={(params) => (
              <TextField
                helperText={error}
                error={hasError}
                FormHelperTextProps={{ style: helperTextStyle }}
                fullWidth
                margin="dense"
                {...params}
              />
            )}
          />
        </LocalizationProvider>
      );
    }

    const isMultiline = type === "textarea";
    const isNumber = type === "number";

    return (
      <TextField
        variant="outlined"
        margin="dense"
        required={required}
        fullWidth
        multiline={isMultiline}
        rows={isMultiline ? rows : undefined}
        placeholder={placeholder}
        autoComplete="off"
        disabled={disabled || loading}
        value={value ?? ""}
        onChange={(e) => {
          const v = e.target.value;
          if (isNumber) {
            onChange(v.replace(/[^0-9]/g, ""));
          } else {
            onChange(v);
          }
        }}
        className={styles.textEnabled}
        helperText={error}
        error={hasError}
        FormHelperTextProps={{ style: helperTextStyle }}
      />
    );
  };

  return (
    <Grid item xs={xs} md={md as any}>
      <label className={styles.label}>{label}</label>
      {renderField()}
      {type === "select" && hasError ? (
        <Box sx={{ display: "flex", direction }}>
          <FormHelperText error>{error}</FormHelperText>
        </Box>
      ) : null}
    </Grid>
  );
};

export default FormField;
